import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, AlertCircle, RefreshCw } from 'lucide-react';
import { Card, Badge } from '../../common';

const HealthStatus = () => {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Detectar se é admin pelo localStorage
  const isAdmin = (() => {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) return false;
      const user = JSON.parse(userStr);
      return user?.type === 'admin' || user?.role === 'admin';
    } catch (error) {
      return false;
    }
  })();

  useEffect(() => {
    checkHealth();
    const interval = setInterval(checkHealth, 30000);
    return () => clearInterval(interval);
  }, []);

  const checkHealth = async () => {
    try {
      setError(null);
      const response = await fetch('https://bot-api.maralabs.com.br/health');
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      setHealth(data);
    } catch (err) {
      console.error('Erro ao verificar saúde da API:', err);
      setError(err.message);
      setHealth({
        status: 'error',
        message: 'Não foi possível conectar ao servidor',
        services: {}
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    if (status === 'healthy' || status === 'ok') {
      return <CheckCircle className="w-5 h-5 text-green-500" />;
    }
    if (status === 'unhealthy' || status === 'down' || status === 'error') {
      return <XCircle className="w-5 h-5 text-red-500" />;
    }
    return <AlertCircle className="w-5 h-5 text-yellow-500" />;
  };

  const getStatusText = (status) => {
    if (status === 'ok' || status === 'healthy') return 'Operacional';
    if (status === 'degraded' || status === 'warning') return 'Degradado';
    return 'Fora do ar';
  };

  if (loading && !health) {
    return (
      <Card>
        <Card.Header>
          <Card.Title>Status do Sistema</Card.Title>
        </Card.Header>
        <Card.Body>
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-5 h-5 text-gray-400 animate-spin" />
            <span className="ml-2 text-sm text-gray-600">Verificando...</span>
          </div>
        </Card.Body>
      </Card>
    );
  }

  const overallStatus = health?.status || 'unknown';
  const isHealthy = overallStatus === 'ok' || overallStatus === 'healthy';

  return (
    <Card>
      <Card.Header>
        <div className="flex items-center justify-between">
          <Card.Title>Status do Sistema</Card.Title>
          <button
            onClick={checkHealth}
            disabled={loading}
            className="p-1 hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
            title="Atualizar"
          >
            <RefreshCw className={`w-4 h-4 text-gray-600 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </Card.Header>
      <Card.Body>
        {error ? (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <XCircle className="w-4 h-4 text-red-600" />
              <span className="text-sm font-semibold text-red-900">Erro de Conexão</span>
            </div>
            <p className="text-xs text-red-700">{error}</p>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                {getStatusIcon(overallStatus)}
                <span className="text-sm font-medium text-gray-700">Status Geral</span>
              </div>
              <Badge variant={isHealthy ? 'success' : 'warning'} dot>
                {getStatusText(overallStatus)}
              </Badge>
            </div>

            {isAdmin && health?.services && Object.keys(health.services).length > 0 && (
              <div className="space-y-2 pt-2 border-t border-gray-200">
                <p className="text-xs font-medium text-gray-500 uppercase">Serviços</p>
                {Object.entries(health.services).map(([service, status]) => (
                  <div key={service} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(status)}
                      <span className="text-sm text-gray-600">
                        {service === 'api' ? 'API' : 
                         service === 'database' ? 'Database' : 
                         service === 'redis' ? 'Redis' : 
                         service === 'evolution' ? 'Evolution API' : 
                         service}
                      </span>
                    </div>
                    <span className={`text-xs font-medium ${
                      status === 'healthy' || status === 'ok' ? 'text-green-600' : 
                      status === 'not_configured' ? 'text-gray-500' : 
                      'text-red-600'
                    }`}>
                      {getStatusText(status)}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <div className="pt-2 border-t border-gray-200">
              <p className="text-xs text-gray-500 text-center">
                Última atualização: {health?.timestamp 
                  ? new Date(health.timestamp).toLocaleTimeString('pt-BR')
                  : new Date().toLocaleTimeString('pt-BR')}
              </p>
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default HealthStatus;